import React, { useState, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Stack,
  IconButton,
  Drawer,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../Components/Custom/CustomButton";
import SocialMediaIcons from "../../Components/SocialMediaIcons";
import {
  ROUTE_LOGIN,
  ROUTE_REGISTRATION,
  ROUTE_PRICING,
  ROUTE_ABOUT_US,
  ROUTE_CONTACT_US,
  ROUTE_VM_ABOUTUS,
  ROUTE_VM_CONTACT_US,
  ROUTE_DEMO,
} from "../../Util/VMConstants";

const LandingToolBar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavigate = useCallback(
    (route, replace = false) => {
      navigate(route, { replace });
      setMobileOpen(false);
    },
    [navigate]
  );

  const handleTryMobile = useCallback(() => {
    const playStoreUrl = "https://play.google.com/store/apps/details?id=com.venuemanager.app&hl=en_IN";
    // if (isMobile) {
      // On mobile, open the Play Store in a new tab
      window.open(playStoreUrl, "_blank", "noopener,noreferrer");
    // } else {
      // On desktop, open the info page in a new tab
      // window.open("/mobile-app", "_blank", "noopener,noreferrer");
    // }
    setMobileOpen(false);
  }, []);

  const navItems = [
    { label: "Home", route: "/landing" },
    { label: "Features", route: ROUTE_PRICING },
    { label: "About us", route: ROUTE_VM_ABOUTUS ?? ROUTE_ABOUT_US },
    { label: "Demo", route: ROUTE_DEMO },
    { label: "Contact us", route: ROUTE_VM_CONTACT_US ?? ROUTE_CONTACT_US },
  ];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "linear-gradient(90deg, #E5FBFF 0%, #FFFFFF 100%)",
          borderBottom: "1px solid #e6e6e6",
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, md: 72 },
            px: { xs: 2, md: 6 },
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* LOGO */}
          <Box
            onClick={() => handleNavigate("/landing", true)}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              src={'/static/vm_logo_black_text_bg.png'}
              alt="Venue Manager"
              sx={{
                height: { xs: 30, md: 40 },
              }}
            />
          </Box>

          {/* DESKTOP NAV */}
          {!isMobile && (
            <Stack direction="row" spacing={2}>
              {navItems.map((item) => (
                <CustomButton
                  key={item.label}
                  variant="text"
                  onClick={() => handleNavigate(item.route)}
                >
                  {item.label}
                </CustomButton>
              ))}
            </Stack>
          )}

          {/* DESKTOP CTA */}
          {!isMobile && (
            <Stack direction="row" spacing={1.5}>
              <Button
                onClick={handleTryMobile}
                sx={{
                  px: 2.5,
                  height: 40,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "999px",
                  background:
                    "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                  color: "#fff",
                }}
              >
                Try in Mobile
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleNavigate(`/${ROUTE_LOGIN}`, true)}
              >
                Log in
              </Button>

              <Button
                variant="contained"
                onClick={() =>
                  handleNavigate(`/${ROUTE_REGISTRATION}`, true)
                }
              >
                Sign up
              </Button>
            </Stack>
          )}

          {/* MOBILE MENU ICON */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileOpen(true)}
              size="large"
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* ================= MOBILE DRAWER ================= */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 280,
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        {/* NAV LINKS */}
        <Stack spacing={0.5}>
          {navItems.map((item) => (
            <Button
              key={item.label}
              fullWidth
              onClick={() => handleNavigate(item.route)}
              sx={{
                justifyContent: "flex-start",
                height: 48,
                fontWeight: 500,
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* CTA BUTTONS */}
        <Stack spacing={1.5}>
          <Button
            fullWidth
            onClick={handleTryMobile}
            sx={{
              height: 48,
              borderRadius: "999px",
              fontWeight: 600,
              textTransform: "none",
              background:
                "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
              color: "#fff",
            }}
          >
            Try in Mobile
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleNavigate(`/${ROUTE_LOGIN}`)}
          >
            Log in
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={() => handleNavigate(`/${ROUTE_REGISTRATION}`)}
          >
            Sign up
          </Button>
        </Stack>

        <Box sx={{ mt: "auto", pt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <SocialMediaIcons brandColors justifyContent="center" />
        </Box>
      </Drawer>
    </>
  );
};

export default LandingToolBar;
